// stores/geofenceStore.js
'use client';
import { fleetApi } from '../api/axiosClient';
import useSWR, { mutate } from 'swr';
import FilterBuilder from '../utils/filterBuilder';
import { normalizeApiResponse } from '../utils/response';
import { apiOslogCrud } from 'helpers/oslogFormatEndpoint';
import { useMemo } from 'react';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

export const revalidateGeofenceList = () => {
  mutate((key) => Array.isArray(key) && key[0] === '/geofence/search');
};

const geofenceApi = apiOslogCrud('geofence');

const fetcher = async ([url, payloadStr]) => {
  const payload = JSON.parse(payloadStr);
  const response = await geofenceApi.search(payload);
  return response;
};

export const useGeofenceList = (page, rowsPerPage, searchKey, searchValue, companyId = '') => {
  const start = page * rowsPerPage;

  const payload = useMemo(() => {
    const fb = new FilterBuilder()
      .setPaging(start, rowsPerPage)
      .addJoin('company', ['name'])
      .addJoin('polygon_type', ['name', 'label', 'description', 'fill_color', 'outline_color'])
      .setOrder(['name'], true);

    if (companyId) {
      const idStr = String(companyId);
      if (idStr.includes(',')) {
        fb.in('company_id', idStr);
      } else {
        fb.equal('company_id', idStr);
      }
    } else {
      fb.filterByCompany();
    }

    const built = fb.build();

    if (searchValue && String(searchValue).trim() !== '') {
      if (!searchKey || searchKey === 'all') {
        built.filters = [
          {
            filters: [
              { name: 'name', logic_operator: 'like', value: searchValue },
              { name: 'geofence_code', logic_operator: 'like', value: searchValue },
              { name: 'address', logic_operator: 'like', value: searchValue },
              { name: 'name', table_name: 'm_company', logic_operator: 'like', value: searchValue },
              { name: 'name', table_name: 'm_polygon_type', logic_operator: 'like', value: searchValue }
            ]
          }
        ];
      } else {
        built.columns = built.columns || [];
        if (searchKey === 'polygon_type_name' || searchKey === 'polygon_type') {
          built.columns.push({ name: 'name', table_name: 'm_polygon_type', logic_operator: 'like', operator: 'AND', value: searchValue });
        } else {
          built.columns.push({ name: searchKey, logic_operator: 'like', operator: 'AND', value: searchValue });
        }
      }
    }

    return built;
  }, [start, rowsPerPage, searchKey, searchValue, companyId]);

  const key = useMemo(() => ['/geofence/search', JSON.stringify(payload)], [payload]);

  const { data, error, isLoading } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    keepPreviousData: true,
    revalidateOnMount: true
  });

  const result = normalizeApiResponse(data, error, isLoading);
  return result;
};

export const getGeofenceById = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const fb = new FilterBuilder().setPaging(0, 1).setOrder(['id'], false).filterByCompany().equal('id', String(id));
    const payload = fb.build();
    const response = await fleetApi.post(`/geofence/search${getTzParam()}`, payload);
    const dataArr = response?.data?.data ?? response?.data ?? (Array.isArray(response) ? response : null);
    const result = Array.isArray(dataArr) && dataArr.length > 0 ? dataArr[0] : null;
    return result;
  } catch (error) {
    console.error('Failed to fetch geofence by ID:', error);
    throw error;
  }
};

export const createGeofence = async (formData = {}) => {
  try {
    const body = {
      ...formData
    };

    const response = await fleetApi.post(`/geofence/new${getTzParam()}`, body);

    if (response?.data?.error === true || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateGeofenceList();
    return response.data;
  } catch (error) {
    console.error('Error creating geofence:', error?.response?.data ?? error);
    throw error;
  }
};

export const updateGeofence = async (id, formData = {}) => {
  if (id == null) throw new Error('id is required');
  try {
    const body = {
      ...formData
    };

    const response = await fleetApi.put(`/geofence/edit/${id}${getTzParam()}`, body);

    if (response?.data?.error === true || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateGeofenceList();
    return response.data;
  } catch (error) {
    console.error('Error updating geofence:', error?.response?.data ?? error);
    throw error;
  }
};

export const deleteGeofence = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.delete(`/geofence/delete/${id}${getTzParam()}`);

    if (response?.data?.error === true || response?.data?.code >= 400 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateGeofenceList();
    return response.data;
  } catch (error) {
    console.error('Error deleting geofence:', error?.response?.data ?? error);
    throw error;
  }
};

export const updateGeomGeofence = async (id, geomData = {}) => {
  if (id == null) throw new Error('id is required');
  try {
    const body = {
      ...geomData
    };
    const response = await fleetApi.put(`/geofence/edit/${id}${getTzParam()}`, body);

    if (response?.data?.error === true || response?.data?.code >= 400 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateGeofenceList();
    return response.data;
  } catch (error) {
    console.error('Error updating geofence geometry:', error?.response?.data ?? error);
    throw error;
  }
};
